import React, { useRef, useState, useEffect } from "react";
import { Volume2, VolumeX, Play, Pause, Instagram, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BUSINESS } from "@/lib/business";

export default function InstagramReel() {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    v.addEventListener("pause", onPause);
    v.addEventListener("play", onPlay);
    return () => {
      v.removeEventListener("pause", onPause);
      v.removeEventListener("play", onPlay);
    };
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  return (
    <section
      id="reel"
      data-testid="instagram-reel-section"
      className="relative py-16 md:py-20 bg-[#080808] border-y border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#e52620]" />
              <span
                data-testid="reel-eyebrow"
                className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold font-mono"
              >
                Unser Reel
              </span>
            </div>
            <h2
              data-testid="reel-title"
              className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95] text-balance"
            >
              MP Carrosserie
              <br />
              <span className="italic font-light text-zinc-400">in 12 Sekunden.</span>
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-lg">
              Wer wir sind, was wir machen — kurz erklärt. Folgen Sie uns auf
              Instagram für mehr Einblicke aus der Werkstatt.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={BUSINESS.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reel-instagram-cta"
                className="inline-flex items-center gap-3 bg-white text-black hover:bg-zinc-200 px-6 py-3.5 rounded-md font-bold uppercase tracking-[0.12em] text-xs transition-all active:scale-95"
              >
                <Instagram className="w-4 h-4" />
                @{BUSINESS.instagramHandle}
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <button
                data-testid="reel-mute-toggle"
                onClick={toggleMute}
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/30 text-white px-5 py-3.5 rounded-md font-bold uppercase tracking-[0.12em] text-xs transition-all"
              >
                {muted ? (
                  <>
                    <VolumeX className="w-3.5 h-3.5" />
                    Ton aktivieren
                  </>
                ) : (
                  <>
                    <Volume2 className="w-3.5 h-3.5 animate-pulse" />
                    Ton aus
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right: phone-frame mockup */}
          <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center">
            <div className="relative" data-testid="reel-phone-frame">
              {/* Glow */}
              <div className="absolute -inset-6 bg-[#e52620]/15 blur-3xl rounded-[3rem]" />

              {/* Phone bezel */}
              <div className="relative w-[260px] md:w-[300px] aspect-[9/19.5] bg-zinc-900 rounded-[2.5rem] p-2.5 shadow-2xl border border-white/10">
                {/* Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-20" />

                {/* Screen */}
                <div className="relative w-full h-full rounded-[1.85rem] overflow-hidden bg-black">
                  <video
                    ref={videoRef}
                    data-testid="reel-video"
                    src="/videos/mp-reel.mp4"
                    autoPlay
                    loop
                    muted={muted}
                    playsInline
                    className="w-full h-full object-cover"
                  />

                  {/* Play/Pause overlay */}
                  <button
                    type="button"
                    onClick={togglePlay}
                    data-testid="reel-play-toggle"
                    className="absolute inset-0 flex items-center justify-center group"
                    aria-label={playing ? "Pause" : "Play"}
                  >
                    <span
                      className={`w-14 h-14 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center transition-opacity duration-300 ${
                        playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"
                      }`}
                    >
                      {playing ? (
                        <Pause className="w-5 h-5 text-white" fill="currentColor" />
                      ) : (
                        <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
                      )}
                    </span>
                  </button>

                  {/* Instagram-like top bar */}
                  <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-white pointer-events-none z-10">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#e52620] flex items-center justify-center font-black text-[10px]">
                        MP
                      </div>
                      <div className="text-[11px] font-bold tracking-tight">
                        {BUSINESS.instagramHandle}
                      </div>
                    </div>
                    <div className="text-[10px] font-mono opacity-70">12 s</div>
                  </div>

                  {/* Bottom mini caption */}
                  <div className="absolute bottom-3 left-3 right-12 text-white pointer-events-none z-10">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#e52620] font-bold">
                      Reel
                    </div>
                    <div className="text-xs font-bold tracking-tight mt-0.5">
                      MP Carrosserie · Wolfwil
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
